import { useState } from "react";
import { Link } from "react-router-dom";
import { errorMessage, useDeadLetters, useRetryDeadLetter, type DeadLetter } from "@/api";
import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { useDocumentTitle } from "@/components/useDocumentTitle";
import { timeAgo } from "@/lib/format";
import { Button, EmptyState, Modal, Mono, Table, useToast, type TableColumn } from "@/ui";
import "./pages.css";

export function DeadLettersPage() {
  useDocumentTitle("Dead letters");
  const dead = useDeadLetters();
  const retry = useRetryDeadLetter();
  const toast = useToast();
  const [retrying, setRetrying] = useState<string | null>(null);
  const [inspect, setInspect] = useState<DeadLetter | null>(null);

  const doRetry = async (d: DeadLetter) => {
    setRetrying(d.stream_id);
    try {
      await retry.mutateAsync(d.stream_id);
      toast.show({ tone: "success", title: "Requeued for delivery", message: d.stream_id });
    } catch (e) {
      toast.show({ tone: "danger", title: "Retry failed", message: errorMessage(e) });
    } finally {
      setRetrying(null);
    }
  };

  const columns: TableColumn<DeadLetter>[] = [
    {
      key: "failed",
      header: "Failed",
      width: "8rem",
      render: (d) => (
        <span className="nowrap" title={d.failed_at}>
          {timeAgo(d.failed_at)}
        </span>
      ),
    },
    {
      key: "message",
      header: "Message",
      render: (d) => (
        <span className="mapping-cell">
          <span className="dl-content">{d.event.content || <em>(no text)</em>}</span>
          <span className="mapping-cell__sub">
            {d.event.author?.display_name ?? "unknown author"} · {d.event.kind}
            {d.mapping_id ? (
              <>
                {" · "}
                <Link className="link" to={`/mappings/${d.mapping_id}`}>
                  mapping {d.mapping_id}
                </Link>
              </>
            ) : null}
          </span>
          <span className="dl-error">{d.error}</span>
        </span>
      ),
    },
    { key: "attempts", header: "Attempts", align: "end", width: "6rem", render: (d) => String(d.attempts) },
    {
      key: "stream",
      header: "Stream id",
      render: (d) => <Mono value={d.stream_id} />,
    },
    {
      key: "actions",
      header: "",
      align: "end",
      render: (d) => (
        <span className="row" style={{ justifyContent: "flex-end" }}>
          <Button size="sm" variant="ghost" onClick={() => setInspect(d)}>
            Inspect
          </Button>
          <Button
            size="sm"
            variant="secondary"
            loading={retrying === d.stream_id}
            disabled={retrying !== null}
            onClick={() => doRetry(d)}
          >
            Retry
          </Button>
        </span>
      ),
    },
  ];

  return (
    <div className="page">
      <PageHeader
        title="Dead letters"
        description="Messages that failed every delivery attempt. Fix the cause (e.g. recreate a webhook), then retry."
      />
      <ErrorBanner error={dead.error} onRetry={() => dead.refetch()} />
      <Table
        caption="Dead letters"
        columns={columns}
        rows={dead.data?.items ?? []}
        getRowId={(d) => d.stream_id}
        loading={dead.isLoading}
        empty={
          <EmptyState
            title="No dead letters"
            description="Every message was delivered or is still being retried."
          />
        }
      />
      <Modal
        open={inspect !== null}
        onClose={() => setInspect(null)}
        title="Dead letter"
        description={inspect?.error}
        size="lg"
      >
        {inspect ? <pre className="code-block">{JSON.stringify(inspect.event, null, 2)}</pre> : null}
      </Modal>
    </div>
  );
}
